import OwnerType from "@/utils/constants/OwnerType.ts";
import PreferencesRequest from "@/utils/constants/PreferencesRequest.ts";
import MetaData from "@/utils/common/MetaData.ts";
import ListPlace from "@/utils/presenters/ListPlace.ts";
import ListReportPlace from "@/utils/presenters/ListReportPlace.ts";
import StorageUtils from "@/utils/common/StorageUtils.ts";
import {ColDef, ColGroupDef, GridState, ValueGetterParams} from "ag-grid-community";
import CacheUtil from "@/utils/login/CacheUtil.ts";
import {CriteriaExpressionSerializer} from "@/utils/urlUtils/CriteriaAndQuestionsURLParser.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import AGGridColumnFactory from "@/utils/UIConstants/AGGridColumns/AGGridColumnFactory.ts";
import {Ref} from "vue";
import AGGridEditableCellLineNumberCellRenderer from "@/components/inputs/AGGridEditableCellLineNumberCellRenderer.vue";
import Translator from "@/utils/common/Translator.ts";
import {QSelectOption, QTreeNode} from "quasar";
import {CustomDTOExpression} from "@/utils/presenters/interfaces.ts";
import ListPresenter from "@/utils/common/ListPresenter.ts";
import AGGridActionsColumnRenderer from "@/components/inputs/AGGridActionsColumnRenderer.vue";
import SecurityUtils from "@/utils/login/SecurityUtils.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import ApplicableWhen from "@/utils/constants/ApplicableWhen.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import StringConstants from "@/utils/constants/StringConstants.ts";
import AbsPresenter from "@/utils/common/AbsPresenter.ts";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import EntityTypesUtils from "@/utils/common/EntityTypesUtils.ts";
import type SearcherViewPresenter from "@/utils/common/SearcherViewPresenter.ts";
import AGGridListRequest = namaTypes.AGGridListRequest;
import DTOCriteria = namaTypes.DTOCriteria;
import ListLayoutPreferences = namaTypes.ListLayoutPreferences;
import ListScreenColumn = namaTypes.ListScreenColumn;
import ViewName = namaTypes.ViewName;
import UIPreferences = namaTypes.UIPreferences;
import FieldMetaData = namaTypes.FieldMetaData;

export default class ListViewUtils {
  static readonly ASC = 'ASC';
  static readonly DESC = 'DESC';
  static readonly REPEAT_COUNT_COLUMN_ID = '__repeatColumn__';
  static CRITERIA_EXP_ID = 1;

  //the timeline preference is kept per list screen, a list that has no timeline config must never inherit it from another list
  static showTimelineStorageKey(layoutId?: string): string {
    return `${IdsAndConstants.SHOW_TIMELINE}-${layoutId ?? ""}`;
  }

  static sortDirections(): QSelectOption[] {
    return [ListViewUtils.ascSortDir(), ListViewUtils.descSortDir()];
  }

  static ascSortDir(): QSelectOption {
    return {label: Translator.translateId(ListViewUtils.ASC), value: ListViewUtils.ASC};
  }

  static descSortDir(): QSelectOption {
    return {label: Translator.translateId(ListViewUtils.DESC), value: ListViewUtils.DESC};
  }

  static getListViewName(viewNames: ViewName[] | undefined): QSelectOption[] {
    let listViewNames: QSelectOption[] = [];
    if (viewNames && viewNames.length > 0) {
      for (let viewName of viewNames) {
        if (viewName.id && viewName.type == "LIST" && viewName.selectable)
          listViewNames.push({label: Translator.translateTitledIDWithPrefixes(viewName, null), value: viewName.id});
      }
    }
    if (listViewNames.length < 1)
      listViewNames.push({label: StringConstants.DEFAULT, value: Translator.translateId(StringConstants.DEFAULT)});
    return listViewNames;
  }

  static async fetchLayoutPreferences(place: ListPlace): Promise<namaTypes.UIPreferences> {
    let req: Record<any, any> = {
      owner: {
        ownerType: OwnerType.Entity,
        ownerId: place.entityType
      },
      properties: [PreferencesRequest.LIST_LAYOUT, PreferencesRequest.LIST_LAYOUT_PREFERENCES, PreferencesRequest.META_DATA, PreferencesRequest.VIEW_NAMES],
      listLayoutScreenName: place.listViewName
    };
    return await MetaData.fetchPreferences(req, (preferences: namaTypes.UIPreferences) => preferences);
  }

  static configureColumnGrouping(columnDefs: (ColDef | ColGroupDef)[]) {
    let mergedColumns: Record<string, ColGroupDef> = {};
    for (let i = 0; i < columnDefs.length; i++) {
      let def = columnDefs[i];
      if (!def.headerName || !def.headerName.includes('|'))
        continue;
      let commonTitle = def.headerName.substring(0, def.headerName.indexOf('|'));
      let parent = mergedColumns[commonTitle];
      if (!parent) {
        parent = mergedColumns[commonTitle] = {
          headerName: commonTitle,
          children: []
        }
        columnDefs[i] = parent;
      } else {
        columnDefs.splice(i, 1);
        i--;
      }
      def.headerName = def.headerName?.substring(commonTitle.length + 1);
      parent.children.push(def);
    }
  }

  static fetchGridStateFromStorage(presenter: AbsPresenter): GridState {
    let place = presenter.fetchPlace() as ListPlace;
    const value: string | null = localStorage.getItem(StorageUtils.calcListViewGridPath(place));
    let state = (value ? JSON.parse(value) : {}) as GridState;
    if (!state.pagination)
      state.pagination = {};
    if (ObjectChecker.isNotNullOrUndefined(place.currentPage)) {
      state.pagination.page = place.currentPage;
    }
    state.pagination.pageSize = place.pageSize || AGGridUtils.fetchDefaultPageSize(presenter);
    return state;
  }

  static persistGridState(place: ListPlace, state: GridState) {
    const stateToStore: GridState = {...state, columnVisibility: state.columnVisibility ?? {hiddenColIds: []}};
    localStorage.setItem(StorageUtils.calcListViewGridPath(place), JSON.stringify(stateToStore));
  }

  static collectDefaultHiddenColIds(columnDefs: (ColDef | ColGroupDef)[]): string[] {
    return columnDefs
      .flatMap(col => this.childrenOfCol(col))
      .filter(col => (col as ColDef).hide === true)
      .map(col => (col as ColDef).colId ?? (col as ColDef).field)
      .filter((colId): colId is string => !!colId);
  }

  static clearColDefsHide(columnDefs: (ColDef | ColGroupDef)[]) {
    for (const col of columnDefs.flatMap(c => this.childrenOfCol(c)))
      (col as ColDef).hide = undefined;
  }

  static buildListInitialGridState(storedState: GridState, defaultHiddenColIds: string[]): GridState {
    if (storedState.columnVisibility)
      return storedState;
    return {...storedState, columnVisibility: {hiddenColIds: defaultHiddenColIds}};
  }

  static getListViewCriteria(place: ListPlace): DTOCriteria | undefined {
    if (!place.criteria)
      return CacheUtil.getCachedCriteria(place.entityType!, place.listViewName);
    else
      return new CriteriaExpressionSerializer().criteriaFromURL(place.criteria);
  }

  static updateCache(request: namaTypes.AGGridListRequest, layoutId?: string) {
    const type = request.entityType;
    if (!type)
      return;
    let uiPreferences: UIPreferences | null = ClientCache.uiPreferencesCache[type];
    if (!uiPreferences) {
      uiPreferences = {}
      ClientCache.uiPreferencesCache[type] = {};
    }
    const listLayoutPreferences: ListLayoutPreferences = {};
    listLayoutPreferences.questionFields = request.questionFields;
    listLayoutPreferences.criteria = request.criteria;
    listLayoutPreferences.sortBy = request.orderBy;
    listLayoutPreferences.layoutId = layoutId;
    listLayoutPreferences.displayedColumns = request.displayedColumns;
    uiPreferences.listLayoutPreferences = listLayoutPreferences;
  }

  static redirectIdSortToNameField(request: AGGridListRequest, entityType?: string) {
    if (!entityType)
      return;
    const nameField = EntityTypesUtils.isDocument(entityType) ? CommonFieldIds.CODE
      : Translator.isArabic() ? CommonFieldIds.NAME1 : CommonFieldIds.NAME2;
    for (const item of request.agGridRequest?.sortModel ?? [])
      if (item.colId === CommonFieldIds.ID)
        item.colId = nameField;
    if (request.orderBy === CommonFieldIds.ID || request.orderBy?.startsWith(CommonFieldIds.ID + ':'))
      request.orderBy = nameField + request.orderBy.substring(CommonFieldIds.ID.length);
  }

  static fetchCurrentSortValueList(sortOptions: Record<string, string>[], sortValue: string) {
    return StringUtils.csvLineToList(sortValue).map(l => sortOptions.find(x => x.value === l)).filter(l => !!(l));
  }

  static childrenOfCol(l: ColDef | ColGroupDef): ColDef[] | ColGroupDef[] {
    if (!(l as ColGroupDef).children)
      return [l];
    return (l as ColGroupDef).children.flatMap(child => this.childrenOfCol(child));
  }

  private static isReportListView(presenter: ListPresenter | SearcherViewPresenter): boolean {
    return presenter instanceof ListPresenter && presenter.fetchPlace() instanceof ListReportPlace;
  }

  private static listLayoutPreferencesOf(presenter: ListPresenter | SearcherViewPresenter): ListLayoutPreferences | undefined {
    return presenter instanceof ListPresenter ? presenter.preferences.listLayoutPreferences : undefined;
  }

  private static isColumnVisibleForSearchContext(column: ListScreenColumn, options: ExtraDisplayColumnMethodOptions): boolean {
    if (!options.forSearcher || ObjectChecker.isEmptyOrNull(column.visibilityRules))
      return true;
    const contextInfo = (options.presenter as SearcherViewPresenter).contextInfo;
    const firstMatchingRule = column.visibilityRules!.find(rule =>
      (ObjectChecker.isEmptyOrNull(rule.entityType) || rule.entityType === contextInfo?.sourceEntityType)
      && (ObjectChecker.isEmptyOrNull(rule.fieldId) || rule.fieldId === contextInfo?.sourceFieldId));
    return firstMatchingRule?.visible ?? false;
  }

  static createDisplayColumns(options: ExtraDisplayColumnMethodOptions) {
    const presenter = options.presenter;
    let columnDefs: (ColDef | ColGroupDef)[] = [];
    if (ObjectChecker.isEmptyOrNull(presenter))
      return columnDefs;
    options.layout = presenter.layout;
    options.isReportListView = this.isReportListView(presenter);
    if (options.editableLineNumber)
      columnDefs.push(this.createEditableLineNumberColumn());
    const TEXT_FIELD = {fieldType: FieldType.Text};
    const REF_FIELD = {fieldType: FieldType.Reference};

    const listLayoutPreferences = this.listLayoutPreferencesOf(presenter);
    const preferredColumnIds: string[] = listLayoutPreferences?.displayedColumns ?? []
    const preferredOrderByColumnId = new Map<string, number>()
    preferredColumnIds.forEach((id, i) => {
      if (!preferredOrderByColumnId.has(id))
        preferredOrderByColumnId.set(id, i)
    })
    const orderedDisplayColumns: ListScreenColumn[] = [...presenter.layout?.displayColumns ?? []].sort((a, b) => {
      const ai = preferredOrderByColumnId.get(a.id ?? '') ?? Number.MAX_SAFE_INTEGER
      const bi = preferredOrderByColumnId.get(b.id ?? '') ?? Number.MAX_SAFE_INTEGER
      return ai - bi
    })

    for (let displayColumn of orderedDisplayColumns) {
      let fieldId = displayColumn.id!;
      if (!SecurityUtils.userCanViewField(LoginUtils.loginInfo?.userInfo, presenter.entityType, fieldId, ApplicableWhen.All))
        continue;
      if (!this.isColumnVisibleForSearchContext(displayColumn, options))
        continue;
      if (!displayColumn.metaData) {
        let extraMetaData: FieldMetaData = TEXT_FIELD;
        if (displayColumn.id === "lastStep.actualResponsible")
          extraMetaData = REF_FIELD;
        else if (presenter.preferences.metaData)
          extraMetaData = MetaData.findFieldMetadata(fieldId, presenter.preferences.metaData);
        displayColumn.metaData = extraMetaData ?? TEXT_FIELD;
      }
      let colDef: ColDef = AGGridColumnFactory.createColDefForType(displayColumn, presenter.entityType, options);
      if (ListViewUtils.isCalculatedAfterQuery(displayColumn))
        colDef.sortable = false;
      if (fieldId.includes('.'))
        colDef.valueGetter = params => params.data?.[fieldId];
      if (listLayoutPreferences && preferredColumnIds.length)
        colDef.hide = !preferredColumnIds.includes(fieldId);
      else if (!displayColumn.displayed)
        colDef.hide = true;
      columnDefs.push(colDef);
    }
    ListViewUtils.configureColumnGrouping(columnDefs);
    if (presenter instanceof ListPresenter && presenter.layout?.actionsColumn?.length)
      columnDefs.push(this.createActionsColumn(presenter))
    return columnDefs;
  }

  static isCalculatedAfterQuery(column: namaTypes.ListScreenColumn): boolean {
    return column.columnType === "Template" || !!column.arabicTemplate || !!column.englishTemplate;
  }

  static createEditableLineNumberColumn(): ColDef {
    let column: ColDef = {};
    column.field = this.REPEAT_COUNT_COLUMN_ID;
    column.colId = this.REPEAT_COUNT_COLUMN_ID;
    column.headerName = '#';
    column.sortable = false;
    column.cellRenderer = AGGridEditableCellLineNumberCellRenderer;
    column.valueGetter = (params: ValueGetterParams) => params.data[this.REPEAT_COUNT_COLUMN_ID];
    column.cellClass = "nama-none-acc";
    column.maxWidth = 100;
    column.resizable = false;
    return column;
  }

  static filterSortOption(value: string | null | undefined, target: Ref<Record<string, string>[]>, originalOptions: Record<string, string>[]) {
    if (!value) {
      target.value = originalOptions;
      return;
    }
    target.value = originalOptions.filter(e =>
      (e.searchData.includes(value)
        || e.searchData.includes(StringUtils.normalizeArabic(value))
        || e.searchData.includes(StringUtils.normalizeArabic(StringUtils.englishToArabic(value)))
        || e.searchData.includes(StringUtils.arabicToEnglish(value).toLowerCase())))
      .sort((a, b) => a.id === b.id ? 0 : a.searchData!.length - b.searchData!.length);
  }

  static isFilterEmpty(criteriaTree: QTreeNode<CustomDTOExpression>[]) {
    return !(criteriaTree && criteriaTree.length && criteriaTree[0].children && criteriaTree[0].children.length);
  }

  private static createActionsColumn(presenter: ListPresenter): ColDef {
    return {
      editable: false,
      colId: IdsAndConstants.ACTIONS_COLUMN,
      cellRenderer: AGGridActionsColumnRenderer,
      cellRendererParams: {
        actions: presenter.layout?.actionsColumn,
        presenter: () => presenter,
      },
      cellClass: "tw-flex tw-items-center nama-full-width-cell",
      autoHeight: true,
    };
  }
}

export type ExtraDisplayColumnMethodOptions =
  {
    editableLineNumber?: boolean,
    forSearcher?: boolean,
    isReportListView?: boolean,
    layout?: namaTypes.ListScreenLayout & namaTypes.SearcherScreenLayout,
    presenter?: ListPresenter | SearcherViewPresenter
  }
